// @ts-strict-ignore
import { JourneyTypesMap } from '@usealloy/typegen';
import { ONGOING_ALERTS_REBRAND } from '~/constants';
import type { FilterConfigByColumn, JourneyFilters } from './types';

const OPTION_ALL = {
  value: 'all',
  label: 'All',
};

export enum DataTypes {
  Timestamp = 'timestamp',
  Range = 'range',
}

const getStatusFilterOptions = ({
  applicationFilters = [],
  isAlertGenerationInJourneysEnabled = false,
}) => {
  const filterOptions = applicationFilters.map(value => {
    const status = value.outcome ?? value.label;
    let newLabel = status;
    if (newLabel === 'Application Review' && isAlertGenerationInJourneysEnabled) {
      newLabel = ONGOING_ALERTS_REBRAND.ALERT_REVIEW;
    }
    return {
      label: newLabel,
      value: status,
      outcome_color: value.outcome_color ? `#${value.outcome_color}` : undefined,
      icon_name: value.outcome_icon ?? undefined,
    };
  });

  return filterOptions.sort((statusA, statusB) => statusA?.label?.localeCompare(statusB?.label));
};

export default function getFilterConfigByColumnOptions({
  applicationFilters,
  featureFlags,
  permissions,
}): FilterConfigByColumn {
  const isAlertGenerationInJourneysEnabled = featureFlags?.alertGenerationInJourneys || featureFlags?.['alert-generation-in-journeys'];

  const journeyApplicationStatusOptions: Record<JourneyFilters, any> = {
    journeyToken: {
      columnDisplayName: 'Journeys',
      defaultOption: OPTION_ALL,
      multiValueOperator: 'or',
      options: [], // Options are populated within the component
      canSearch: true,
      dependentFilters: ['journeyVersion'],
    },
    journeyType: {
      columnDisplayName: 'Journey Type',
      defaultOption: { label: 'Application', value: JourneyTypesMap.APPLICATION },
      options: [
        { label: 'Alert', value: JourneyTypesMap.ALERT },
      ],
    },
    journeyVersion: {
      columnDisplayName: 'Journey Versions',
      defaultOption: OPTION_ALL,
      multiValueOperator: 'or',
      disabledWhenNoOptions: true,
      options: [], // Options are populated within the table component
    },
    branchName: {
      columnDisplayName: 'Branch Names',
      defaultOption: OPTION_ALL,
      multiValueOperator: 'or',
      disabledWhenNoOptions: true,
      hidden: true,
      options: [], // Options are populated within the table component
    },
    journeyApplicationCreatedAt: {
      columnDisplayName: 'Created at',
      dataType: DataTypes.Timestamp,
    },
    journeyApplicationUpdatedAt: {
      columnDisplayName: 'Updated at',
      dataType: DataTypes.Timestamp,
    },
    journeyApplicationIsArchived: {
      columnDisplayName: 'Archived',
      defaultOption: OPTION_ALL,
      options: [
        { label: 'Archived', value: 'true' },
        { label: 'Not Archived', value: 'false' },
      ],
    },
    journeyApplicationIsSandbox: {
      columnDisplayName: 'Environment',
      defaultOption: OPTION_ALL,
      options: [
        { label: 'Live', value: 'false' },
        { label: 'Sandbox', value: 'true' },
      ],
    },
    assignedAgent: {
      canSearch: true,
      columnDisplayName: 'Assignee',
      defaultOption: OPTION_ALL,
      multiValueOperator: 'or',
      options: [
        { label: 'Assigned', value: 'assigned' },
        { label: 'Unassigned', value: 'unassigned' },
      ], // Agent options are added within the table component
    },
    journeyChampionChallengerName: {
      canSearch: true,
      columnDisplayName: 'Champion Challenger Test',
      defaultOption: OPTION_ALL,
      multiValueOperator: 'or',
      disabledWhenNoOptions: true,
      options: [], // Options are populated within the table component
      hidden: true,
    },
    journeyApplicationTags: {
      canSearch: true,
      columnDisplayName: 'Tags',
      defaultOption: OPTION_ALL,
      multiValueOperator: 'or',
      options: [], // Options are populated within the table component
      hidden: true,
    },
    journeyApplicationReasons: {
      canSearch: true,
      columnDisplayName: 'Outcome Reasons',
      defaultOption: OPTION_ALL,
      multiValueOperator: 'or',
      options: [], // Options are populated within the table component
      hidden: true,
    },
    customerPartnerId: {
      canSearch: true,
      columnDisplayName: 'Partner ID',
      defaultOption: OPTION_ALL,
      multiValueOperator: 'or',
      options: [], // Options are populated within the table component
      hidden: true,
    },
    childEntityType: {
      columnDisplayName: 'Type',
      defaultOption: OPTION_ALL,
      multiValueOperator: 'or',
      options: [
        {
          label: 'Person',
          value: 'person',
        },
        {
          label: 'Business',
          value: 'business',
        },
      ],
    },
    journeyApplicationStatus: {
      columnDisplayName: 'Status',
      defaultOption: OPTION_ALL,
      multiValueOperator: 'or',
      options: getStatusFilterOptions({
        applicationFilters,
        isAlertGenerationInJourneysEnabled,
      }),
    },
    journeyApplicationDataSources: {
      columnDisplayName: 'Data Sources',
      defaultOption: OPTION_ALL,
      multiValueOperator: 'or',
      options: [], // Options are populated within the table component
      hidden: true,
      canSearch: true,
    },
    journeyApplicationReviewReasons: {
      columnDisplayName: 'Review Reasons',
      defaultOption: OPTION_ALL,
      multiValueOperator: 'or',
      options: [], // Options are populated within the table component
      hidden: true,
      canSearch: true,
      hasPermission: !!permissions['READ:CustomerReasons:null'],
    },
    journeyApplicationReasonCodes: {
      columnDisplayName: 'Reason Codes',
      defaultOption: OPTION_ALL,
      multiValueOperator: 'or',
      options: [], // Options are populated within the table component
      hidden: true,
      canSearch: true,
      hasPermission: !!permissions['READ:CustomerReasonCodeGroups:null'],
    },
    journeyApplicationFraudScores: {
      columnDisplayName: 'Fraud Scores',
      multiValueOperator: 'and',
      options: [], // Options are populated within the table component
      hidden: true,
      dataType: DataTypes.Range,
      optionsAsFacets: true, // Each option is added to the filters row as an independent filter when selected
    },
    evaluationResponseStatus: {
      columnDisplayName: 'Evaluation Response Status',
      defaultOption: OPTION_ALL,
      options: [
        { label: 'Success', value: 'success' },
        { label: 'Partial', value: 'partial' },
      ],
      hidden: true,
    },
    afefCustomerSlugs: {
      columnDisplayName: 'Customer',
      defaultOption: OPTION_ALL,
      multiValueOperator: 'or',
      options: [], // Options are populated within the component
      canSearch: true,
    },
    journeyApplicationIsRerun: {
      columnDisplayName: 'Rerun',
      defaultOption: OPTION_ALL,
      options: [
        { label: 'Rerun', value: true },
        { label: 'Original', value: false },
      ],
      hidden: true,
    },
  };
  return journeyApplicationStatusOptions;
}
